import { Rank } from '../../sim/defs';
import { defineEnemy, Motion } from '../schema';

/** E-M5 — FAUCON. Le prédateur qui STOOPE.
 *
 *  Le Dard est le contre au camping : il descend, marque une pause, puis plonge
 *  DROIT depuis une colonne fixe, sans jamais tenir compte de l'endroit où se
 *  trouve le joueur. Le Faucon va bien plus loin : il TRAQUE. Il descend
 *  lentement en se calant sur l'horizontale du joueur, se verrouille au-dessus de
 *  lui, PIQUE à pleine vitesse — plus vite que le Dard — puis, s'il a manqué, se
 *  RETIRE vers le haut en laissant une fenêtre franche.
 *
 *  ── Ce qu'il enseigne ──
 *  Qu'un corps peut CHASSER, pas seulement tomber. Contre le Dard, il suffit de
 *  ne pas se tenir dans sa colonne. Contre le Faucon, la colonne EST la tienne :
 *  il se met au-dessus de toi. La parade n'est plus de choisir sa place à
 *  l'avance mais de casser le verrou au dernier moment — exactement au départ du
 *  piqué —, ce qui est la même compétence que l'esquive tardive du missile
 *  guidé, appliquée cette fois à une masse.
 *
 *  ── Pourquoi le piqué est DROIT, et pas re-visé ──
 *  Le verrou se fait pendant l'APPROCHE, par la position : le Faucon se place
 *  au-dessus du joueur, puis COMMET une plongée verticale qu'il ne corrige plus.
 *  Un piqué qui continuerait à suivre serait un missile à tête chercheuse
 *  corporel — inévitable, donc la suppression de l'esquive. La borne horizontale
 *  de la traque (`TRACK_SPEED`, sous la vitesse du joueur) est l'autre garde-fou :
 *  un joueur qui glisse plus vite que le Faucon ne peut se caler le sème avant
 *  même le piqué.
 *
 *  ── Pourquoi il n'a pas d'émetteur ──
 *  Comme le Dard, sa pression est PUREMENT corporelle. Lui ajouter un tir
 *  brouillerait la seule chose qu'il demande de lire : la trajectoire.
 *
 *  ── Le hook plutôt qu'une forme du moteur (PLAN.md §3.3) ──
 *  Traque → piqué → retrait est une machine à trois états qu'aucune forme de
 *  mouvement ne porte. On l'exprime dans `onUpdate` — pur, sans allocation, sans
 *  la moindre transcendante (que des comparaisons et de l'arithmétique). La
 *  DESCENTE, elle, reste au moteur (Linear) : elle apporte gratuitement
 *  l'accélération d'entrée et la protection d'apparition, et le hook n'ajoute que
 *  ce que le moteur ne sait pas faire — la traque, la surcharge du piqué, la
 *  remontée. On ne réécrit jamais ce que le moteur sait déjà faire. */

/** Instant du départ du piqué, en secondes depuis l'apparition. La longue
 *  approche traquée EST le télégraphe — le contraste descente lente → piqué est
 *  la seule chose que le joueur doit anticiper, exactement comme la pause du
 *  Dard. */
const CHARGE_AT = 2.6;
/** Durée du piqué, en secondes : de quoi traverser la rangée du joueur de part
 *  en part avant de se retirer. */
const CHARGE_FOR = 0.8;
/** Vitesse horizontale de traque, en u/s. Sous la vitesse du joueur (480) : le
 *  verrou est réel mais jamais parfait — c'est ce qui garde l'esquive vivante. */
const TRACK_SPEED = 210;
/** Surcharge de vitesse verticale ajoutée à la descente du moteur pendant le
 *  piqué, en u/s. Descente (230) + surcharge (540) = 770 u/s, franchement
 *  au-dessus des 720 du Dard : le Faucon est le corps le plus rapide du
 *  bestiaire, et sa vitesse ne se joue qu'en anticipation. */
const CHARGE_BOOST = 540;
/** Remontée du retrait, en u/s (net, après retrait de la descente du moteur, il
 *  reste ~290 u/s vers le haut). */
const RETREAT_UP = 520;
/** Dérive latérale du retrait, en u/s : le Faucon s'écarte vers le bord en
 *  remontant, ce qui rouvre franchement le centre où le joueur s'est réfugié. */
const RETREAT_SIDE = 260;

export default defineEnemy({
  id: 'faucon',
  rank: Rank.Standard,
  hp: 80,
  visualRadius: 20,
  score: 160,
  telegraph: 0.8,
  drops: 1,
  // La descente d'approche vient du moteur : accélération d'entrée et protection
  // d'apparition gratuites. Le hook ne touche QUE la traque, le piqué et le
  // retrait — jamais la descente de base.
  motion: { kind: Motion.Linear, speed: 230 },
  onUpdate: (slot, age, dt, ctx) => {
    let x = ctx.x[slot]!;
    let y = ctx.y[slot]!;

    if (age < CHARGE_AT) {
      // APPROCHE : on se cale sur l'horizontale du joueur, d'un pas BORNÉ (un
      // joueur plus rapide sème le verrou). La descente reste celle du moteur.
      const dx = ctx.playerX - x;
      const step = TRACK_SPEED * dt;
      if (dx > step) x += step;
      else if (dx < -step) x -= step;
      else x = ctx.playerX;
      ctx.x[slot] = x;
    } else if (age < CHARGE_AT + CHARGE_FOR) {
      // PIQUÉ : on plonge droit, sans plus suivre — le joueur s'en sort d'un pas
      // de côté, comme contre le Dard. La surcharge s'AJOUTE à la descente du
      // moteur ; l'abscisse est GELÉE là où l'approche l'a laissée (colonne
      // commise).
      y -= CHARGE_BOOST * dt;
      ctx.y[slot] = y;
    } else {
      // RETRAIT : on remonte et on dérive vers le bord, laissant une fenêtre
      // franche. La remontée finit par le faire sortir par le haut du cadre.
      y += RETREAT_UP * dt;
      x += (x < 0 ? -1 : 1) * RETREAT_SIDE * dt;
      ctx.x[slot] = x;
      ctx.y[slot] = y;
    }
  },
});
